import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLeagueStrict, useIsWatcher, useLeagueRefresh } from "@/lib/league-context";
import { useAuth } from "@/lib/auth-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { createEntry } from "@/lib/entries.functions";
import {
  getMyLeagueProfile,
  ensureMyLeagueProfile,
} from "@/lib/league-profile.functions";
import { EntryForm } from "@/components/EntryForm";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/_app/$league/entries/new")({
  component: NewEntryPage,
  head: () => ({
    meta: [{ title: "New bracket — Bracket Goblin" }],
  }),
});

function NewEntryPage() {
  const league = useLeagueStrict();
  const navigate = useNavigate();
  const createFn = useAuthedServerFn(createEntry);
  const getProfileFn = useAuthedServerFn(getMyLeagueProfile);
  const ensureProfileFn = useAuthedServerFn(ensureMyLeagueProfile);
  const refreshViewerHasEntry = useLeagueRefresh();
  const { profile } = useAuth();
  const realName = `${profile?.first_name ?? ""} ${profile?.last_name ?? ""}`.trim();
  const [busy, setBusy] = useState(false);
  const isWatcher = useIsWatcher();

  // null = still loading; { needsSetup: false } = row exists, skip block;
  // { needsSetup: true, realName } = no row, show the block.
  const [setupState, setSetupState] = useState<
    | { needsSetup: false }
    | { needsSetup: true; realName: string }
    | null
  >(null);

  useEffect(() => {
    if (isWatcher) {
      void navigate({ to: "/$league", params: { league: league.slug }, replace: true });
    }
  }, [isWatcher, league.slug, navigate]);

  useEffect(() => {
    if (isWatcher) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getProfileFn({ data: { leagueId: league.id } });
        if (cancelled) return;
        if (p) {
          setSetupState({ needsSetup: false });
        } else {
          setSetupState({ needsSetup: true, realName });
        }
      } catch {
        // If we can't determine, fail safe by skipping the block so the
        // user isn't blocked from creating their entry.
        if (!cancelled) setSetupState({ needsSetup: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isWatcher, league.id, getProfileFn, realName]);

  if (isWatcher) return null;
  if (setupState === null) {
    return (
      <main className="mx-auto max-w-xl px-4 py-16 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
        Loading…
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-xl px-4 py-6 sm:py-10">
      <Link
        to="/$league"
        params={{ league: league.slug }}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to league
      </Link>
      <h1 className="mt-3 mb-1 font-display text-2xl font-bold text-foreground sm:text-3xl">
        New bracket
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Create a bracket for {league.name}.
      </p>
      <EntryForm
        submitLabel="Create bracket"
        busy={busy}
        allowProxy={(league.maxEntriesPerMember ?? 1) > 1}
        displayNameSetup={
          setupState.needsSetup ? { realName: setupState.realName } : undefined
        }
        onCancel={() => navigate({ to: "/$league", params: { league: league.slug } })}
        onSubmit={async (values) => {
          setBusy(true);
          try {
            // Persist display-name choice FIRST so the privacy preference
            // is saved even if entry creation fails afterwards.
            if (values.displayNameSetup) {
              await ensureProfileFn({
                data: {
                  leagueId: league.id,
                  displayName: values.displayNameSetup.displayName || undefined,
                  showRealName: values.displayNameSetup.showRealName,
                },
              });
            }
            const created = await createFn({
              data: {
                leagueId: league.id,
                bracketName: values.bracketName,
                entryType: values.entryType,
                proxyDisplayName: values.proxyDisplayName || undefined,
                relationship: values.relationship || undefined,
              },
            });
            toast.success("Bracket created.");
            track("entry_created", {
              leagueId: league.id,
              leagueSlug: league.slug,
              tournamentType: league.tournamentType,
              entryType: values.entryType,
              isProxy: values.entryType === "for_someone_else",
            });
            refreshViewerHasEntry();
            const goGroups =
              league.tournamentType === "fifa_wc_mens_group_knockout" &&
              !league.knockoutPickable;
            await navigate({
              to: goGroups ? "/$league/groups" : "/$league/bracket",
              params: { league: league.slug },
              search: { entry: created.id },
            });
          } catch (err) {
            toast.error(
              err instanceof Error ? err.message : "Something went wrong.",
            );
            throw err;
          } finally {
            setBusy(false);
          }
        }}
      />
    </main>
  );
}
